import { ScenarioAllocatedUpgrade } from "in_queue/types/scenarioResultType";

export interface UpgradeContribution {
    projectId: string;
    cost: number;
    /** Share of this upgrade's total cost allocated to `projectId`, 0..100. */
    pct: number;
}

export interface UpgradeContributionRow {
    upgradeId: string;
    name: string;
    /** Sum of allocated cost across all contributing projects. */
    totalCost: number;
    /** Subtotal allocated to the current project. */
    myCost: number;
    /**
     * Contributions sorted with the current project first, then descending
     * by allocated cost. Multiple `scenario_allocated_upgrades` rows for
     * the same `(upgrade, project)` pair (one per constraint_type) are
     * collapsed into a single contribution with summed cost.
     */
    contributions: UpgradeContribution[];
}

/**
 * Group scenario-allocated upgrades by `networkUpgradeId` and compute
 * per-project contributions, returning one row per upgrade.
 *
 * Each row exposes `totalCost` (sum across all projects), `myCost` (sum
 * for the current project), and a sorted `contributions[]` array. The
 * current project is pinned first in `contributions` so charts can render
 * "you" as the leading segment of a stacked bar without re-sorting.
 *
 * Note: `scenario_allocated_upgrades` is keyed by
 * `(network_upgrade_id, project_id, constraint_type)`, so the same
 * project can legitimately appear more than once for one upgrade (e.g.
 * an ERIS row + an NRIS row). We collapse those into a single
 * contribution per project so the stacked bar / breakdown table don't
 * render duplicate segments / rows.
 *
 * Rows are returned in input-encounter order. Callers can sort / filter
 * (e.g. "top 5 by myCost") downstream.
 */
export const aggregateUpgradesByContribution = (
    upgrades: ReadonlyArray<ScenarioAllocatedUpgrade>,
    currentProjectId: string
): UpgradeContributionRow[] => {
    interface Bucket {
        upgradeId: string;
        name: string;
        totalCost: number;
        myCost: number;
        byProject: Map<string, UpgradeContribution>;
    }
    const byUpgrade = new Map<string, Bucket>();
    for (const u of upgrades) {
        let bucket = byUpgrade.get(u.networkUpgradeId);
        if (!bucket) {
            bucket = {
                upgradeId: u.networkUpgradeId,
                name: u.networkUpgradeName,
                totalCost: 0,
                myCost: 0,
                byProject: new Map<string, UpgradeContribution>()
            };
            byUpgrade.set(u.networkUpgradeId, bucket);
        }
        const existing = bucket.byProject.get(u.projectId);
        if (existing) {
            existing.cost += u.allocatedCost;
        } else {
            bucket.byProject.set(u.projectId, {
                projectId: u.projectId,
                cost: u.allocatedCost,
                pct: 0 // filled in below
            });
        }
        bucket.totalCost += u.allocatedCost;
        if (u.projectId === currentProjectId) {
            bucket.myCost += u.allocatedCost;
        }
    }

    const rows: UpgradeContributionRow[] = [];
    for (const bucket of Array.from(byUpgrade.values())) {
        const contributions = Array.from(bucket.byProject.values());
        if (bucket.totalCost > 0) {
            for (const c of contributions) {
                c.pct = (c.cost / bucket.totalCost) * 100;
            }
        }
        // Current project first, then descending by cost.
        contributions.sort((a, b) => {
            if (a.projectId === currentProjectId) return -1;
            if (b.projectId === currentProjectId) return 1;
            return b.cost - a.cost;
        });
        rows.push({
            upgradeId: bucket.upgradeId,
            name: bucket.name,
            totalCost: bucket.totalCost,
            myCost: bucket.myCost,
            contributions
        });
    }
    return rows;
};
